import type { TelemetryEvent } from '@voxa/shared';
import { api } from './api.ts';

/**
 * Buffered, numbers-only telemetry (section 10). Nothing here may carry
 * speech: the shared schema has no field for it, and this module never builds
 * one. Failures are swallowed — a measurement that cannot be delivered must
 * not break a call.
 */
export class Telemetry {
  #roomId: string | null = null;
  #sessionId: string | null = null;
  #queue: TelemetryEvent[] = [];
  #timer: ReturnType<typeof setInterval> | null = null;

  bind(roomId: string | null, sessionId: string | null = null): void {
    this.#roomId = roomId;
    this.#sessionId = sessionId;
  }

  start(intervalMs = 10_000): void {
    this.#timer ??= setInterval(() => void this.flush(), intervalMs);
  }

  stop(): void {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
    void this.flush();
  }

  record(name: string, values?: Record<string, number>, tags?: Record<string, string>): void {
    this.#queue.push({ name, at: Date.now(), ...(values && { values }), ...(tags && { tags }) });
    // The server takes 50 at a time; past that, drop the oldest rather than
    // grow a buffer forever on a page nobody is watching.
    if (this.#queue.length > 200) this.#queue.splice(0, this.#queue.length - 200);
    if (this.#queue.length >= 50) void this.flush();
  }

  async flush(): Promise<void> {
    if (this.#queue.length === 0) return;
    const events = this.#queue.splice(0, 50);
    try {
      await api.telemetry({
        ...(this.#roomId ? { roomId: this.#roomId } : {}),
        ...(this.#sessionId ? { sessionId: this.#sessionId } : {}),
        events,
      });
    } catch {
      // Deliberately silent. Losing a metric is cheaper than a visible error.
    }
  }
}

export const telemetry = new Telemetry();
